import type { ReactNode } from 'react'

type PunkTabColour = 'chartreuse' | 'purple' | 'green'
type PunkTabCorner = 'tl' | 'tr'
type PunkTabSize = 'sm' | 'md' | 'lg'

type PunkTabProps = {
  children: ReactNode
  colour?: PunkTabColour
  corner?: PunkTabCorner
  rotation?: string
  size?: PunkTabSize
  /**
   * Card index in its row/grid. Drives the default brand-colour cycle
   * (chartreuse → purple → green) and the rocking rotation that alternates
   * direction between cards. Pass `colour` or `rotation` to override.
   */
  index?: number
}

const COLOUR_CLASS: Record<PunkTabColour, string> = {
  chartreuse: 'bg-brand-chartreuse-vivid text-[#1a0e20]',
  purple: 'bg-brand-purple text-white',
  green: 'bg-brand-green text-white',
}

const SIZE_CLASS: Record<PunkTabSize, string> = {
  sm: 'size-7 text-[0.8rem]',
  md: 'size-9 text-[1rem]',
  lg: 'size-12 text-[1.35rem]',
}

const COLOUR_CYCLE: readonly PunkTabColour[] = ['chartreuse', 'purple', 'green']

/**
 * Sticker-style accent pinned to a card corner. A square in a brand colour,
 * slightly rotated, with a punk hard shadow. Parent card needs
 * `position: relative` so the absolute positioning anchors correctly.
 */
export function PunkTab({
  children,
  colour,
  corner = 'tl',
  rotation,
  size = 'md',
  index = 0,
}: PunkTabProps) {
  const cornerClass = corner === 'tl' ? '-top-2 -left-2' : '-top-2 -right-2'
  const resolvedColour = colour ?? COLOUR_CYCLE[index % 3]
  const resolvedRotation = rotation ?? `${(index % 2 === 0 ? -1 : 1) * (6 + (index % 3))}deg`
  return (
    <span
      aria-hidden="true"
      className={`font-marker absolute ${cornerClass} z-[2] inline-flex ${SIZE_CLASS[size]} items-center justify-center rounded-sm font-bold shadow-[2px_2px_0_rgba(0,0,0,0.3)] ring-1 ring-black/15 ${COLOUR_CLASS[resolvedColour]}`}
      style={{ rotate: resolvedRotation }}
    >
      {children}
    </span>
  )
}
